import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { Alert, Box, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";

import { useOutletApi } from "@/features/authentication";
import { ChartOfAccountTypeEnum } from "@/features/chart-of-accounts";
import { OutletChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";
import { formatNumber } from "@/features/shared";

// Same set the purchase-invoice form offers when an expense line is authored by
// hand, so a copied line can land anywhere a typed one could.
const ALLOWED_OCOA_TYPES = [
  ChartOfAccountTypeEnum.REVENUE,
  ChartOfAccountTypeEnum.COST_OF_GOODS_SOLD,
  ChartOfAccountTypeEnum.EXPENSES,
  ChartOfAccountTypeEnum.OTHER_EXPENSES,
  ChartOfAccountTypeEnum.OTHER_INCOME,
  ChartOfAccountTypeEnum.OTHER_CURRENT_ASSETS,
  ChartOfAccountTypeEnum.OTHER_ASSETS,
  ChartOfAccountTypeEnum.CURRENT_LIABILITIES,
  ChartOfAccountTypeEnum.LONG_TERM_LIABILITIES,
  ChartOfAccountTypeEnum.INVENTORY,
];

/**
 * Maps each of the seller's expense lines onto one of the buyer's own accounts.
 *
 * The two sides cannot share an account: outlet chart-of-account rows are
 * per-outlet, so the seller's id never resolves under the buyer even when both
 * charts carry the same code. The amount is never sent — the backend reads it
 * from the source invoice — so this section only ever chooses accounts.
 */
export const ExpenseAccountsSection = ({ buyerId }) => {
  const { control, setValue, getValues } = useFormContext();
  const outletApi = useOutletApi(buyerId);
  const { fields } = useFieldArray({ control, name: "expenseAccounts" });
  const [prefillFailed, setPrefillFailed] = useState(false);

  // Prefill by matching account code: when both outlets were cloned from the
  // same master chart the codes line up and there is nothing left to pick. Only
  // ever fills empty rows, so a user's own choice is never overwritten.
  const didPrefill = useRef(false);
  useEffect(() => {
    if (didPrefill.current || !buyerId || fields.length === 0) return;
    didPrefill.current = true;

    let cancelled = false;
    (async () => {
      try {
        const {
          data: { data },
        } = await outletApi.get("chart-of-accounts", {
          params: { leafOnly: true, types: ALLOWED_OCOA_TYPES },
        });
        if (cancelled) return;

        const byCode = new Map((data || []).map((account) => [account.code, account]));
        getValues("expenseAccounts").forEach((row, index) => {
          if (row.outletChartOfAccount) return;
          const match = byCode.get(row.sellerAccountCode);
          if (match) {
            setValue(`expenseAccounts.${index}.outletChartOfAccount`, match);
          }
        });
      } catch {
        // Prefill is a convenience, not a requirement — the pickers still work.
        if (!cancelled) setPrefillFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [buyerId, fields.length, getValues, outletApi, setValue]);

  if (fields.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Faktur penjualan ini tidak memiliki biaya lain-lain.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Pilih akun milik outlet pembeli untuk setiap biaya. Nilainya mengikuti
        faktur penjualan dan tidak dapat diubah di sini.
      </Typography>

      {prefillFailed && (
        <Alert severity="info">
          Gagal memuat akun pembeli untuk pengisian otomatis. Silakan pilih akun
          secara manual.
        </Alert>
      )}

      <DataGrid
        rows={fields}
        getRowId={(row) => row.id}
        getRowHeight={() => "auto"}
        columns={[
          {
            field: "sellerAccount",
            headerName: "Akun Penjual",
            flex: 2.5,
            sortable: false,
            valueGetter: (_, row) =>
              row.sellerAccountCode
                ? `${row.sellerAccountCode} - ${row.sellerAccountName}`
                : "-",
          },
          {
            field: "amount",
            headerName: "Nominal (Rp)",
            flex: 1,
            type: "number",
            sortable: false,
            valueFormatter: (value) => formatNumber(value),
          },
          {
            field: "note",
            headerName: "Catatan",
            flex: 1.5,
            sortable: false,
            valueGetter: (note) => note || "-",
          },
          {
            field: "outletChartOfAccount",
            headerName: "Akun Pembeli",
            flex: 2.5,
            sortable: false,
            renderCell: ({ id }) => {
              const index = fields.findIndex((field) => field.id === id);
              if (index === -1) return null;

              return (
                <Box sx={{ width: "100%", py: 1 }}>
                  <Controller
                    control={control}
                    name={`expenseAccounts.${index}.outletChartOfAccount`}
                    render={({ field, fieldState }) => (
                      <OutletChartOfAccountSelect
                        outletId={buyerId}
                        types={ALLOWED_OCOA_TYPES}
                        value={field.value}
                        onChange={field.onChange}
                        TextFieldProps={{
                          required: true,
                          error: fieldState.invalid,
                          helperText: fieldState.error?.message,
                          InputProps: { placeholder: "Pilih Akun" },
                        }}
                      />
                    )}
                  />
                </Box>
              );
            },
          },
        ]}
        disableRowSelectionOnClick
        hideFooter
        // The picker owns focus inside its cell; the grid's own focus ring on
        // top of the input border reads as a rendering glitch.
        sx={{ "& .MuiDataGrid-cell:focus-within": { outline: "none" } }}
      />
    </Stack>
  );
};

ExpenseAccountsSection.propTypes = {
  buyerId: PropTypes.string,
};
